/* eslint-disable spire/export-styles */
import IconFuel from "@affinity/constants/IconFuel";
import * as styleHelpers from "@affinity/constants/StyleHelpers";
import { BuildPersitenceUrl, getQueryStringFromState } from "@affinity/Store/PrePopulation";
import { CustomState } from "@affinity/Store/Reducers";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button from "@insite/mobius/Button";
import { HasHistory, useHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import * as React from "react";
import { FC } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: CustomState) => ({
    queryString: getQueryStringFromState(state),
});

const fields = {
    variant: "variant",
    label: "label",
    link: "link",
    alignment: "alignment",
    iconVariant: "iconVariant",
    iconLocation: "iconLocation",
    variantSize: "variantSize",
} as const;

export const iconVariants = [
    { displayName: "Arrow left", value: "ChevronLeft" },
    { displayName: "Arrow right", value: "ChevronRight" },
    { displayName: "Phone", value: "Phone" },
    { displayName: "Smartphone", value: "Smartphone" },
    { displayName: "Fuel", value: "IconFuel" },
    { displayName: "Search", value: "Search" },
    { displayName: "Cart", value: "ShoppingCart" },
    { displayName: "User", value: "User" },
    { displayName: "Award", value: "Award" },
    { displayName: "Mail", value: "Mail" },
];

interface OwnProps extends WidgetProps {
    fields: {
        [fields.variant]: "primary" | "secondary" | "tertiary";
        [fields.label]: string;
        [fields.link]: LinkFieldValue;
        [fields.alignment]: string;
        [fields.iconVariant]: typeof iconVariants[number]["value"];
        [fields.iconLocation]: "left" | "right";
        [fields.variantSize]: "medium" | "large";
    };
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    HasHistory;

export interface ButtonStyles {
    wrapperStyles: InjectableCss;
}

const wrapperStyles: InjectableCss = {};

export const CmsQueryButton: FC<Props> = ({ fields, queryString }) => {
    const history = useHistory();
    const link = useGetLink(fields.link);
    const { isInShell } = useShellContext();

    const onClick = () => {
        if (!link.url) {
            return;
        }

        let builtUrl = link.url;
        switch (queryString?.page ?? "") {
            case "placeorder":
                builtUrl = BuildPersitenceUrl(link.url, queryString!);
                break;
            default:
                // No action, use default
        }
        if (builtUrl.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = builtUrl;
        } else {
            history.push(builtUrl);
        }
    };

    const styles = useMergeStyles("button", { wrapperStyles });
    let iconOptions;
    let buttonStyles;
    if (fields.iconVariant) {
        switch (fields.iconVariant) {
            // Handle custom icons that can be loaded by passing a react component
            case "IconFuel":
                iconOptions = { src: IconFuel, position: fields.iconLocation };
                buttonStyles = css`
                    svg {
                        fill: currentColor;
                    }
                `;
                break;
            // Handle no icon
            case "None":
                iconOptions = undefined;
                break;
            // Handle OOTB Icons that can be loaded by name
            default:
                iconOptions = { src: fields.iconVariant, position: fields.iconLocation };
        }
    }

    return (
        <ContentWrapper alignment={fields.alignment} size={fields.variantSize} {...styles?.wrapperStyles}>
            <Button
                variant={fields.variant}
                sizeVariant={fields.variantSize}
                onClick={onClick}
                icon={iconOptions}
                mergeCss={true}
                css={buttonStyles}
            >
                {fields.label}
            </Button>
        </ContentWrapper>
    );
};

export const ContentWrapper = styled.div<InjectableCss & { alignment: string; size: string }>`
    display: flex;
    width: 100%;
    justify-content: ${props => props.alignment ?? "flex-start"};
    padding: ${styleHelpers.rem(10)} 0;
    ${injectCss};
`;

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withHistory(CmsQueryButton)),
    definition: {
        group: "Basic",
        icon: "rectangle-wide",
        fieldDefinitions: [
            {
                name: fields.variant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                defaultValue: "primary",
                fieldType: "General",
            },
            {
                name: fields.label,
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.link,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                allowUrls: () => false, // Disable URLs feature
                allowCategories: () => false, // Disable Categories feature
            },
            {
                name: fields.alignment,
                displayName: "Button Alignment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
                defaultValue: "primary",
                fieldType: "General",
            },
            {
                name: fields.iconVariant,
                displayName: "Icon Variant",
                editorTemplate: "DropDownField",
                options: iconVariants,
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.iconLocation,
                displayName: "Icon Location",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Right", value: "right" },
                ],
                defaultValue: "left",
                fieldType: "General",
            },
            {
                name: fields.variantSize,
                displayName: "Variant Size",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Default", value: "medium" },
                    { displayName: "Large", value: "large" },
                ],
                defaultValue: "medium",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
